import type { DictFragment } from '../translations';

/**
 * ProjectDetailPage.tsx (projectDetail.*) and ProjectForm.tsx (projectForm.*).
 * Generic strings live in `common.*` — check there before adding a new key here.
 */
export const projects: DictFragment = {
  fr: {
    // ── projectDetail: PersonAllocations (cross-project allocation expand) ──
    'projectDetail.alloc.notAllocatedElsewhere': "Actuellement affecté·e à aucun autre projet.",
    'projectDetail.alloc.colProject': "Projet",
    'projectDetail.alloc.colPhase': "Étape",
    'projectDetail.alloc.colRole': "Rôle",
    'projectDetail.alloc.colHours': "Heures",
    'projectDetail.alloc.committedElsewhere': "Engagé·e ailleurs",

    // ── projectDetail: ProjectTasksPanel ──
    'projectDetail.tasksPanel.heading': "Tâches",
    'projectDetail.tasksPanel.headingCount': "Tâches ({n})",
    'projectDetail.tasksPanel.boardLink': "Tableau des tâches →",
    'projectDetail.tasksPanel.boardOpenHelp': "Les tâches du projet par étape (To Do, In Progress, Done…)",
    'projectDetail.tasksPanel.none': "Aucune tâche pour le moment.",
    'projectDetail.tasksPanel.showAll': "Tout afficher ({n})",

    // ── projectDetail: banners / feedback / not found ──
    'projectDetail.changeRequestBanner': "Vous proposez des modifications. Un administrateur examinera votre demande.",
    'projectDetail.changeRequestSuccess': "Demande de modification envoyée avec succès. Un administrateur examinera votre demande.",
    'projectDetail.changeRequestFailed': "Échec de l'envoi de la demande de modification",
    'projectDetail.notFound': "Projet introuvable.",
    'projectDetail.privateNotMember': "« {name} » est un projet privé : seules les personnes qui en font partie peuvent l’ouvrir.",
    'projectDetail.overviewOnly': "Vous ne faites pas partie de ce projet : seul son aperçu est visible.",
    'projectDetail.privateCardOnly': "Projet privé : vous n’en faites pas partie, seule sa carte est visible.",

    // ── projectDetail: header (name + stage tabs) ──
    'projectDetail.initiativeLabel': "Initiative",
    'projectDetail.initiativeNamePlaceholder': "Nom de l'initiative",
    'projectDetail.tabOverview': "Aperçu",
    'projectDetail.boardButton': "Kanban du projet",
    'projectDetail.tabPrequalification': "Préqualification",
    'projectDetail.tabScoping': "Cadrage",
    'projectDetail.tabQualification': "Qualification",
    'projectDetail.tabPlanning': "Planification",
    'projectDetail.tabExecution': "Exécution",
    'projectDetail.tabHandover': "Transfert",
    'projectDetail.copyStageLink': "Copier le lien vers cette étape",
    'share.button': 'Partager',
    'share.help': 'Partager ce projet sur Slack et par e-mail',
    'share.title': 'Partager ce projet',
    'share.toLabel': 'Avec',
    'share.toPlaceholder': 'Choisir une ou plusieurs personnes…',
    'share.notePlaceholder': 'Ce que vous voulez lui dire…',
    'share.explain': 'Chaque personne reçoit le nom du projet, votre note et un lien vers cette étape. Le partage est noté dans l’historique du projet.',
    'share.sent': 'Partagé avec {name} ({via}).',
    'share.channelFailed': 'Pas envoyé à {name} par {channel} : {reason}',
    'share.skipped': 'Pas envoyé à {name} : {reason}',
    'share.err.noRecipient': 'Choisissez au moins une personne.',
    'share.err.noEmail': 'aucune adresse e-mail connue.',
    'share.err.noAccess': 'ce projet est privé et cette personne n’en fait pas partie.',
    'share.err.notOnSlack': 'aucun compte Slack ne correspond à son e-mail.',

    // ── projectDetail: Overview tab — stage / gate ──
    'projectDetail.overview.stageLabel': "Étape du projet",
    'projectDetail.overview.stageHelp': "Cliquez sur une étape pour avancer ou revenir en arrière — votre travail dans les autres étapes est préservé et réapparaît lorsque vous y retournez.",
    'projectDetail.overview.advanceButton': "Avancer vers {label} →",
    'projectDetail.overview.missingHelp': "Pour avancer vers {label} : ajoutez {missing}.",
    'projectDetail.overview.domainLabel': "Domaine du portefeuille",
    'projectDetail.overview.selectDomainPlaceholder': "Sélectionner un domaine",
    'projectDetail.overview.levelLabel': "Niveau stratégique",
    'projectDetail.overview.selectLevelPlaceholder': "Sélectionner un niveau",

    // ── projectDetail: Overview tab — problem / need ──
    'projectDetail.overview.problemSectionLabel': "Problème ou besoin",
    'projectDetail.overview.problemLabel': "Description du problème / besoin",
    'projectDetail.overview.problemPlaceholder': "Quel problème ou besoin cette initiative adresse-t-elle ?",
    'projectDetail.overview.goalLabel': "Objectif",
    'projectDetail.overview.goalPlaceholder': "Quel est le résultat souhaité ?",
    'projectDetail.overview.valueAddedLabel': "Valeur ajoutée",

    // ── projectDetail: Overview tab — deliverable / resources / ownership ──
    'projectDetail.overview.deliverableSectionLabel': "Livrable",
    'projectDetail.overview.deliverableFieldLabel': "Ce qui existera à la fin",
    'projectDetail.overview.resourcesSectionLabel': "Ressources et effort (estimation)",
    'projectDetail.overview.effortSizeLabel': "Taille de l'effort (jours-homme)",
    'projectDetail.overview.timelineLabel': "Délai jusqu'à l'achèvement",
    'projectDetail.overview.cashBudgetLabel': "Budget financier",
    'projectDetail.overview.cashBudgetPlaceholder': "ex. 50k, 200k EUR",
    'projectDetail.overview.teamAllocationLabel': "Allocation de l'équipe",
    'projectDetail.overview.collaboratorsLabel': "Collaborateurs",
    'projectDetail.overview.ownershipSectionLabel': "Responsabilité",
    'projectDetail.overview.sponsorLabel': "Sponsor",
    'projectDetail.overview.sponsorPlaceholder': "Qui sponsorise ceci ?",
    'projectDetail.overview.pmoAccompanistLabel': "Accompagnant PMO",
    'projectDetail.overview.pmoAccompanistPlaceholder': "Choisir un accompagnant",
    'projectDetail.overview.userGroupsLabel': "Utilisateur·rice·s / Groupes d'utilisateurs",
    'projectDetail.overview.userGroupsPlaceholder': "Saisissez et appuyez sur Entrée",

    // ── projectDetail: Préqualification tab — decision ──
    'projectDetail.prequalDecision.sectionLabel': "Décision",
    'projectDetail.prequalDecision.contactedSponsor': "Sponsor contacté pour clarifications",
    'projectDetail.prequalDecision.help': "Prenez une décision lorsque vous êtes prêt·e — cela enregistre votre travail de préqualification et fait avancer le projet à l'étape suivante.",
    'projectDetail.prequalDecision.advanceButton': "Avancer vers Cadrage →",
    'projectDetail.prequalDecision.deferButton': "Reporter au backlog",
    'projectDetail.prequalDecision.currentVerdictLabel': "Verdict actuel : ",
    'projectDetail.prequalDecision.verdictAdvance': "Avancer vers Cadrage",
    'projectDetail.prequalDecision.verdictDeferred': "Reporté",
    'projectDetail.prequalDecision.verdictLabel': "Verdict",
    'projectDetail.prequalDecision.advanceTitle': "Avancer vers Cadrage",
    'projectDetail.prequalDecision.advanceModalMessage': "Cela enregistre votre travail de préqualification et fait avancer le projet à l'étape de Cadrage.",
    'projectDetail.prequalDecision.deferTitle': "Reporter le projet",
    'projectDetail.prequalDecision.deferModalMessage': "Cela déplace le projet vers Reporté — il sera examiné lors de la prochaine réunion du Conseil de Pilotage.",
    'projectDetail.prequalDecision.deferConfirmLabel': "Reporter",

    // ── projectDetail: Scoping tab ──
    'projectDetail.scoping.reminderLabel': "Rappel de l’Aperçu (lecture seule)",
    'projectDetail.scoping.reminderDeliverables': "Livrables",
    'projectDetail.scoping.sectionLabel': "Cadrage",
    'projectDetail.scoping.inScopeLabel': "Dans le périmètre",
    'projectDetail.scoping.inScopePlaceholder': "Ce qui est inclus dans le périmètre",
    'projectDetail.scoping.outOfScopeLabel': "Hors périmètre",
    'projectDetail.scoping.outOfScopePlaceholder': "Ce qui est exclu du périmètre",
    'projectDetail.scoping.urgencyLabel': "Urgence",
    'projectDetail.scoping.urgencyLow': "Faible",
    'projectDetail.scoping.urgencyMedium': "Moyenne",
    'projectDetail.scoping.urgencyHigh': "Élevée",
    'projectDetail.scoping.interventionLabel': "Intervention PMO",
    'projectDetail.scoping.interventionWeekly': "Hebdomadaire",
    'projectDetail.scoping.interventionBiWeekly': "Bimensuelle",
    'projectDetail.scoping.interventionMonthly': "Mensuelle",
    'projectDetail.scoping.startMonth': "Début (mois)",
    'projectDetail.scoping.endMonth': "Fin (mois)",
    'projectDetail.scoping.cashRequested': "Budget financier (demandé à l'intake)",
    'projectDetail.scoping.noneDash': "— aucun —",
    'projectDetail.scoping.availableBudgetLabel': "Budget disponible (confirmé avec la finance)",
    'projectDetail.scoping.availableBudgetPlaceholder': "ex. 40k confirmés disponibles",
    'projectDetail.scoping.financeNotesLabel': "Notes financières",
    'projectDetail.scoping.financeNotesPlaceholder': "Source du budget, approbations, contraintes…",
    'projectDetail.scoping.allocationsLabel': "Allocations",
    'projectDetail.scoping.allocHintWithWeeks': "{hpw}h semaine · {weeks} semaines sur l'horizon temporel",
    'projectDetail.scoping.allocHintNoWeeks': "{hpw}h semaine · définissez un mois de début et de fin ci-dessus pour calculer les heures",
    'projectDetail.scoping.colPerson': "Personne",
    'projectDetail.scoping.colAllocation': "Allocation",
    'projectDetail.scoping.colHoursPerWeek': "h / semaine",
    'projectDetail.scoping.colTotalHours': "Total h",
    'projectDetail.scoping.rolePlaceholder': "rôle",
    'projectDetail.scoping.showOtherProjectsTitle': "Afficher les autres projets de cette personne",
    'projectDetail.scoping.removePersonTitle': "Retirer la personne (aussi de l'intake)",
    'projectDetail.scoping.addPersonPlaceholder': "Ajouter une autre personne par son nom…",
    'projectDetail.scoping.totalHoursLabel': "Total des heures",
    'projectDetail.scoping.totalHoursSetHorizon': "Total des heures — définissez l'horizon temporel",
    'projectDetail.scoping.advanceHelp': "Une fois le cadrage terminé, avancez vers Qualification — cela enregistre vos modifications, et le projet apparaît alors au Conseil de Pilotage pour que le conseil le qualifie.",
    'projectDetail.scoping.advanceButton': "Avancer vers Qualification →",
    'projectDetail.scoping.saveNeedsBothDates': "renseignez une date de début et une date de fin d'horizon temporel",
    'projectDetail.scoping.saveNeedsStartDate': "renseignez une date de début d'horizon temporel",
    'projectDetail.scoping.saveNeedsEndDate': "renseignez une date de fin d'horizon temporel",
    'projectDetail.scoping.saveNeedsPeople': "ajoutez au moins une personne aux allocations",
    'projectDetail.scoping.saveNeedsAllAllocations': "renseignez l'allocation de temps de chaque personne",
    'projectDetail.scoping.saveBlockedHint': "Cadrage — sauvegarde impossible : {issues}",

    // ── projectDetail: Qualification tab ──
    'projectDetail.qualification.sectionLabel': "Qualification",
    'projectDetail.qualification.badgeBacklogged': "Mis en attente",
    'projectDetail.qualification.badgeQualified': "Qualifié pour exécution",
    'projectDetail.qualification.actionBacklogged': "Mis en attente",
    'projectDetail.qualification.actionQualified': "Qualifié pour exécution",
    'projectDetail.qualification.onDate': "{label} le {date}",
    'projectDetail.qualification.pending': "En attente de qualification par le Conseil de Pilotage.",
    'projectDetail.qualificationComingSoon': "Les détails de Qualification seront disponibles ici dans une future mise à jour.",

    // ── projectDetail: Planning tab ──
    'projectDetail.planning.sectionLabel': "Planification",
    'projectDetail.planning.milestonesLabel': "Jalons",
    'projectDetail.planning.advanceHelp': "Une fois la planification terminée, faites avancer le projet vers Exécution — cela enregistre vos modifications et fait progresser le projet.",
    'projectDetail.planning.advanceButton': "Avancer vers Exécution →",

    // ── projectDetail: Execution tab ──
    'projectDetail.execution.statusLabel': "Statut du projet",
    'projectDetail.execution.atRisk': "À risque — un jalon est en retard",
    'projectDetail.execution.onTrack': "Sur la bonne voie",
    'projectDetail.execution.pmoStatusLabel': "Statut PMO",
    'projectDetail.execution.pmoStatusHelp': "Rouge / Orange / Vert, évalué par l'opérateur lors des réunions PMO. Affiché sur les cartes de dépendances de ce projet.",
    'projectDetail.execution.green': "Vert",
    'projectDetail.execution.orange': "Orange",
    'projectDetail.execution.red': "Rouge",

    // ── projectDetail: Handover tab ──
    'projectDetail.handover.outcomeSectionLabel': "Résultat — améliorations obtenues",
    'projectDetail.handover.outcomeHint': "Les améliorations définies en Préqualification. Enregistrez le score réellement atteint (par défaut, la cible).",
    'projectDetail.handover.noImprovements': "Aucune amélioration stratégique n'a été définie pour ce projet.",
    'projectDetail.handover.achievedLabel': "atteint",
    'projectDetail.handover.resourcesTitle': "Ressources",
    'projectDetail.handover.notesLabel': "Notes de transfert",
    'projectDetail.handover.notesPlaceholder': "Notes de résultat / leçons apprises…",
    'projectDetail.handover.recordHelp': "Enregistrer les résultats réécrit les scores atteints dans RCAJO (son compartiment scores-PMO, daté et lié à ce projet) et clôture le projet.",
    'projectDetail.handover.recordButton': "Enregistrer les résultats dans RCAJO et clôturer →",

    // ── projectDetail: Footer ──
    'projectDetail.requestChange': "Demander une modification",
    'projectDetail.saveChanges': "Enregistrer les modifications",
    'projectDetail.visibility': "Visibilité",
    'projectDetail.visibilityPublic': "Public",
    'projectDetail.visibilityPrivate': "Privé",
    'projectDetail.visibilityPrivateHelp': "Seuls les collaborateurs impliqués dans ce projet (et les admins) peuvent le voir.",
    'projects.privateBadge': "Projet privé",
    'projectDetail.blockedMode': "Mode bloqué",
    'projectDetail.blockedBadge': "Bloqué",
    'projectDetail.blockedReasonPlaceholder': "Pourquoi ce projet est-il bloqué ?",
    'projectDetail.markBlocked': "Marquer comme bloqué",
    'projectDetail.unblock': "Débloquer",

    // ── projectDetail: confirm modals & stage-advance messages ──
    'projectDetail.advanceTitleTo': "Avancer vers {target}",
    'projectDetail.revertTitleTo': "Revenir à {target}",
    'projectDetail.advance': "Avancer",
    'projectDetail.revert': "Revenir",
    'projectDetail.revertMessage': "Ramenez le projet à {target}. Votre travail dans les étapes suivantes est conservé (juste masqué) et réapparaît lorsque vous y retournez.",
    'projectDetail.advanceForwardMessage': "Faites avancer le projet vers {target}. Les étapes que vous sautez restent vides jusqu'à ce qu'elles soient remplies.",
    'projectDetail.advanceMsg.qualification': "Cela déplace le projet vers Qualification, où le Conseil de Pilotage l'examine et le qualifie. Votre travail de cadrage est enregistré.",
    'projectDetail.advanceMsg.execution': "Cela déplace le projet vers Exécution. Votre planification est enregistrée.",
    'projectDetail.advanceMsg.handover': "Tous les jalons sont terminés — cela déplace le projet vers Transfert. Votre travail est enregistré.",
    'projectDetail.advanceMsg.generic': "Cela enregistre votre travail et déplace le projet vers {target}.",
    'projectDetail.deleteInitiativeTitle': "Supprimer l'initiative",
    'projectDetail.deleteInitiativeMessage': "Supprimer cette initiative ? Cette action est irréversible.",
    'projectDetail.recordCloseTitle': "Enregistrer les résultats et clôturer",
    'projectDetail.recordCloseMessage': "Cela enregistre les scores atteints dans RCAJO (compartiment scores-PMO, daté et lié à ce projet) et clôture le projet.",
    'projectDetail.recordAndClose': "Enregistrer et clôturer",

    // ── projectForm: shared building blocks ──
    'projectForm.addQuoted': "Ajouter « {q} »",
    'projectForm.bullet.remove': "Retirer",
    'projectForm.bullet.addOutput': "Ajouter un résultat",
    'projectForm.searchProjectsPlaceholder': "Rechercher des projets…",
    'projectForm.collaborators.label': "Collaborateurs impliqués",
    'projectForm.collaborators.searchPlaceholder': "Saisissez un nom pour rechercher…",

    // ── projectForm: stage-gate "missing" field labels ──
    'projectForm.gate.name': "nom",
    'projectForm.gate.problem': "problème",
    'projectForm.gate.goal': "objectif",
    'projectForm.gate.deliverables': "livrables",
    'projectForm.gate.valueAdded': "valeur ajoutée",
    'projectForm.gate.effortEstimate': "estimation de l'effort",
    'projectForm.gate.sponsor': "sponsor",
    'projectForm.gate.verdictAdvance': "un verdict « Avancer vers Cadrage »",
    'projectForm.gate.inScope': "dans le périmètre",
    'projectForm.gate.outOfScope': "hors périmètre",
    'projectForm.gate.urgency': "urgence",
    'projectForm.gate.interventionMode': "mode d'intervention PMO",
    'projectForm.gate.timeHorizon': "horizon temporel",
    'projectForm.gate.atLeastOneAllocation': "au moins une allocation",
    'projectForm.gate.atLeastOneMilestone': "au moins un jalon",
    // ── projectDetail: stage bar, topic tabs and Aperçu synthesis ──
    'projectDetail.stage.inbox': "Demande",
    'projectDetail.stage.prequalification': "Préqualification",
    'projectDetail.stage.scoping': "Cadrage",
    'projectDetail.stage.qualification': "Qualification",
    'projectDetail.stage.planning': "Planification",
    'projectDetail.stage.execution': "Exécution",
    'projectDetail.stage.handover': "Transfert",
    'projectDetail.stage.closed': "Clôturé",
    'projectDetail.stage.deferred': "Reporté",
    'projectDetail.stage.backlog': "En attente",
    'projectDetail.stageBar.ariaSteps': "Étapes du projet",
    'projectDetail.stageBar.toNext': "Pour passer en {stage}",
    'projectDetail.stageBar.advanceTo': "Passer en {stage}",
    'projectDetail.stageBar.completeFirst': "Complétez d'abord les éléments manquants",
    'projectDetail.stageBar.jumpTo': "Aller à l'endroit où cela se remplit",
    'projectDetail.stageBar.milestonesDone': "jalons terminés ({done}/{total})",
    'projectDetail.stageBar.executionHelp': "Les jalons se clôturent dans le suivi de Plan et exécution.",
    'projectDetail.stageBar.prequalTitle': "Décision de préqualification",
    'projectDetail.stageBar.prequalHelp': "Évaluez l'intérêt stratégique et les dépendances (onglet À faire), puis décidez. La décision enregistre votre travail et déplace le projet.",
    'projectDetail.stageBar.qualificationTitle': "En attente du Conseil de Pilotage",
    'projectDetail.stageBar.qualificationHelp': "Le Conseil qualifie le projet pour l'exécution ou le met en attente.",
    'projectDetail.stageBar.openCouncil': "Ouvrir le Conseil",
    'projectDetail.stageBar.handoverTitle': "Clôture du projet",
    'projectDetail.stageBar.handoverHelp': "Saisissez les améliorations obtenues dans l'onglet Clôture, puis enregistrez-les dans RCAJO.",
    'projectDetail.stageBar.openResults': "Saisir les résultats",
    'projectDetail.stageBar.recordAndClose': "Enregistrer et clôturer",
    'projectDetail.stageBar.closedTitle': "Projet clôturé",
    'projectDetail.stageBar.deferredTitle': "Projet reporté",
    'projectDetail.stageBar.deferredHelp': "Il sera réexaminé lors du prochain Conseil de Pilotage.",
    'projectDetail.stageBar.backlogTitle': "Projet en backlog",
    'projectDetail.stageBar.backlogHelp': "Mis en attente : il reprendra lorsqu'une place se libère.",
    'projectDetail.topic.apercu': "Aperçu",
    'projectDetail.topic.etape': "À faire",
    'projectDetail.topic.etapeCurrent': "À faire · {stage}",
    'projectDetail.topic.etapeOther': "Étape · {stage}",
    'projectDetail.stageBar.viewStage': "Voir l'étape {stage} : ce qu'elle demande",
    'projectDetail.stageView.titleCurrent': "{stage} · ce qu'il faut faire",
    'projectDetail.stageView.titlePast': "{stage} · étape terminée",
    'projectDetail.stageView.titleFuture': "{stage} · étape à venir",
    'projectDetail.stageView.backToCurrent': "Revenir à l'étape en cours ({stage})",
    'projectDetail.stageView.pastNote': "Ces informations restent modifiables. Chaque modification est enregistrée dans la chronologie du projet.",
    'projectDetail.stageView.futureNote': "Cette étape s'ouvrira plus tard ; le projet est à l'étape {stage}. Voici ce qu'elle demandera :",
    'projectDetail.stageView.optional': "facultatif",
    'projectDetail.stageView.strategic': "Vérification stratégique",
    'projectDetail.stageView.sequencing': "Dépendances et séquençage",
    'projectDetail.stageView.decision': "Décision",
    'projectDetail.stageView.next': "Passer à l'étape suivante",
    'projectDetail.stageView.help.Inbox': "Décrivez la demande : le problème et l'objectif, ce qui sera livré, le sponsor et l'effort estimé. Une fois ces points remplis, la demande passe en Préqualification.",
    'projectDetail.stageView.help.Préqualification': "Évaluez l'intérêt stratégique de la demande (capacités et ressources RCAJO) et ses dépendances avec d'autres projets, puis décidez : la poursuivre en Cadrage ou la reporter.",
    'projectDetail.stageView.help.Scoping': "Précisez le périmètre, le calendrier et l'équipe avec ses allocations. Le projet passe ensuite en Qualification devant le Conseil de pilotage.",
    'projectDetail.stageView.help.Qualification': "Le Conseil de pilotage examine le projet et le qualifie pour l'exécution ou le place dans le backlog. La décision se prend dans le Conseil.",
    'projectDetail.stageView.help.Planning': "Découpez le projet en jalons qui couvrent les livrables. Il faut au moins un jalon pour passer en Exécution.",
    'projectDetail.stageView.help.Handover': "Indiquez les résultats atteints et les notes de transfert, puis clôturez le projet.",
    'projectDetail.stageSetTitle': "Étape du projet",
    'projectDetail.history.button': "Historique ({n})",
    'projectDetail.history.title': "Modifié après son étape",
    'projectDetail.history.empty': "(vide)",
    'projectDetail.history.field.problem': "Description du problème",
    'projectDetail.history.field.goal': "Objectif",
    'projectDetail.history.field.value_added': "Valeur ajoutée",
    'projectDetail.history.field.deliverables': "Livrables",
    'projectDetail.history.field.effort_size': "Estimation de l'effort",
    'projectDetail.history.field.timeline': "Délai jusqu'à l'achèvement",
    'projectDetail.history.field.cash': "Budget demandé",
    'projectDetail.history.field.pmo_accompanist': "Accompagnant PMO",
    'projectDetail.history.field.user_group': "Groupes d'utilisateurs",
    'projectDetail.history.field.contacted_sponsor': "Sponsor contacté",
    'projectDetail.history.field.prequal_notes': "Notes de préqualification",
    'projectDetail.history.field.rcajo': "Vérification stratégique",
    'projectDetail.history.field.sequencing': "Dépendances et séquençage",
    'projectDetail.history.field.scope_includes': "Dans le périmètre",
    'projectDetail.history.field.scope_excludes': "Hors périmètre",
    'projectDetail.history.field.urgency': "Urgence",
    'projectDetail.history.field.intervention_mode': "Intervention PMO",
    'projectDetail.history.field.time_horizon_start': "Début (mois)",
    'projectDetail.history.field.time_horizon_end': "Fin (mois)",
    'projectDetail.history.field.available_budget': "Budget disponible",
    'projectDetail.history.field.cash_notes': "Notes financières",
    'projectDetail.history.field.allocations': "Allocations de l'équipe",
    'projectDetail.history.field.check_in_cadence': "Cadence de suivi",
    'projectDetail.history.field.health': "Statut PMO",
    'projectDetail.history.field.handover_notes': "Notes de transfert",
    'projectDetail.stageSetHelp': "Place le projet directement à une étape, sans passer par les conditions (PMO). Le changement est enregistré dans la chronologie.",
    'projectDetail.topic.contenu': "Contenu",
    'projectDetail.topic.equipe': "Équipe et moyens",
    'projectDetail.topic.strategie': "Stratégie",
    'projectDetail.topic.plan': "Plan et exécution",
    'projectDetail.topic.cloture': "Clôture",
    'projectDetail.topics.lockedUntil': "À compléter à l'étape {stage}",
    'projectDetail.topics.opensAt': "S'ouvre à l'étape {stage}",
    'projectDetail.topics.lockedDesc.apercu': "",
    'projectDetail.topics.lockedDesc.contenu': "",
    'projectDetail.topics.lockedDesc.equipe': "",
    'projectDetail.topics.lockedDesc.strategie': "L'évaluation stratégique (RCAJO), le séquencement et la décision de préqualification.",
    'projectDetail.topics.lockedDesc.plan': "Les jalons du projet, puis leur suivi avec les tâches et les documents pendant l'exécution.",
    'projectDetail.topics.lockedDesc.cloture': "Les améliorations obtenues et les notes de transfert, enregistrées à la clôture.",
    'projectDetail.topics.problemGoal': "Problème et objectif",
    'projectDetail.topics.classification': "Classement",
    'projectDetail.topics.team': "Équipe",
    'projectDetail.topics.allocationsLater': "Les rôles et les pourcentages d'allocation se fixent à l'étape {stage}.",
    'projectDetail.topics.calendar': "Calendrier",
    'projectDetail.topics.budgetEffort': "Budget et effort",
    'projectDetail.topics.followUp': "Suivi",
    'projectDetail.topics.editPlan': "Modifier le plan",
    'projectDetail.topics.settings': "Paramètres du projet",
    'projectDetail.topics.unsaved': "Modifications non enregistrées",
    'projectDetail.topics.allSaved': "Tout est enregistré",
    'projectDetail.synth.atStage': "à l'étape {stage}",
    'projectDetail.synth.health': "Statut PMO",
    'projectDetail.synth.nextMilestone': "Prochain jalon",
    'projectDetail.synth.allMilestonesDone': "Tous terminés",
    'projectDetail.synth.progress': "Avancement",
    'projectDetail.synth.milestonesCount': "{done} / {total} jalons",
    'projectDetail.synth.projectBlock': "Le projet",
    'projectDetail.synth.scopeIn': "Inclus",
    'projectDetail.synth.scopeOut': "Exclu",
    'projectDetail.synth.classification': "Classement",
    'projectDetail.synth.team': "Équipe",
    'projectDetail.synth.horizon': "Horizon",
    'projectDetail.synth.requested': "(souhaité)",
    'projectDetail.synth.budgetRequested': "Budget demandé",
    'projectDetail.synth.budgetAvailable': "Budget disponible",
    'projectDetail.synth.decisions': "Décisions",
    'projectDetail.synth.pending': "En cours",
    'projectDetail.synth.pendingInBar': "À prendre dans la barre d'étapes",
    'projectDetail.synth.edit': "Modifier",
    'projectDetail.synth.open': "Ouvrir",
    'projectDetail.synth.plannedEnd': "Fin prévue",
    'projectDetail.synth.milestones': "Jalons",
    'projectDetail.synth.milestonesLater': "Les jalons se définissent à l'étape {stage}.",
    'projectDetail.synth.noMilestones': "Aucun jalon pour le moment.",
    'projectDetail.synth.noDeliverables': "Aucun livrable défini.",
    'projectDetail.synth.coveredBy': "Jalon {list}",
    'projectDetail.synth.ms.done': "Terminé",
    'projectDetail.synth.ms.active': "En cours",
    'projectDetail.synth.ms.late': "En retard",
    'projectDetail.synth.ms.pending': "À venir",
    'projectDetail.topics.deliverablesToCover': "Livrables à couvrir",
    'projectDetail.topics.deliverables': "Livrables",
    'projectDetail.topics.milestonesAndTasks': "Jalons et tâches",
    'projectDetail.blockedReasonLabel': "Raison du blocage",
    'projectDetail.deleteInitiativeHelp': "Supprime définitivement cette initiative. Cette action est irréversible.",
  },
  en: {
    // ── projectDetail: PersonAllocations (cross-project allocation expand) ──
    'projectDetail.alloc.notAllocatedElsewhere': "Not currently allocated to any other project.",
    'projectDetail.alloc.colProject': "Project",
    'projectDetail.alloc.colPhase': "Phase",
    'projectDetail.alloc.colRole': "Role",
    'projectDetail.alloc.colHours': "Hours",
    'projectDetail.alloc.committedElsewhere': "Committed elsewhere",

    // ── projectDetail: ProjectTasksPanel ──
    'projectDetail.tasksPanel.heading': "Tasks",
    'projectDetail.tasksPanel.headingCount': "Tasks ({n})",
    'projectDetail.tasksPanel.boardLink': "Task board →",
    'projectDetail.tasksPanel.boardOpenHelp': "The project's tasks by stage (To Do, In Progress, Done…)",
    'projectDetail.tasksPanel.none': "No tasks yet.",
    'projectDetail.tasksPanel.showAll': "Show all ({n})",

    // ── projectDetail: banners / feedback / not found ──
    'projectDetail.changeRequestBanner': "You are proposing changes. An admin will review your request.",
    'projectDetail.changeRequestSuccess': "Change request submitted successfully. An admin will review your request.",
    'projectDetail.changeRequestFailed': "Failed to submit change request",
    'projectDetail.notFound': "Project not found.",
    'projectDetail.privateNotMember': "“{name}” is a private project: only the people who are part of it can open it.",
    'projectDetail.overviewOnly': "You’re not part of this project: only its overview is visible.",
    'projectDetail.privateCardOnly': "Private project: you’re not part of it, only its card is visible.",

    // ── projectDetail: header (name + stage tabs) ──
    'projectDetail.initiativeLabel': "Initiative",
    'projectDetail.initiativeNamePlaceholder': "Initiative name",
    'projectDetail.tabOverview': "Overview",
    'projectDetail.boardButton': "Project board",
    'projectDetail.tabPrequalification': "Préqualification",
    'projectDetail.tabScoping': "Scoping",
    'projectDetail.tabQualification': "Qualification",
    'projectDetail.tabPlanning': "Planning",
    'projectDetail.tabExecution': "Execution",
    'projectDetail.tabHandover': "Handover",
    'projectDetail.copyStageLink': "Copy link to this stage",
    'share.button': 'Share',
    'share.help': 'Share this project on Slack and by e-mail',
    'share.title': 'Share this project',
    'share.toLabel': 'With',
    'share.toPlaceholder': 'Choose one or more people…',
    'share.notePlaceholder': 'What you want to tell them…',
    'share.explain': 'Each person gets the project’s name, your note and a link to this stage. The share is noted in the project’s history.',
    'share.sent': 'Shared with {name} ({via}).',
    'share.channelFailed': 'Not sent to {name} by {channel}: {reason}',
    'share.skipped': 'Not sent to {name}: {reason}',
    'share.err.noRecipient': 'Choose at least one person.',
    'share.err.noEmail': 'no e-mail address is known.',
    'share.err.noAccess': 'this project is private and they are not part of it.',
    'share.err.notOnSlack': 'no Slack account matches their e-mail.',

    // ── projectDetail: Overview tab — stage / gate ──
    'projectDetail.overview.stageLabel': "Project stage",
    'projectDetail.overview.stageHelp': "Click a stage to advance or revert — your work in other stages is preserved and reappears when you return.",
    'projectDetail.overview.advanceButton': "Advance to {label} →",
    'projectDetail.overview.missingHelp': "To advance to {label}: add {missing}.",
    'projectDetail.overview.domainLabel': "Portfolio Domain",
    'projectDetail.overview.selectDomainPlaceholder': "Select domain",
    'projectDetail.overview.levelLabel': "Strategic Level",
    'projectDetail.overview.selectLevelPlaceholder': "Select level",

    // ── projectDetail: Overview tab — problem / need ──
    'projectDetail.overview.problemSectionLabel': "Problem or Need",
    'projectDetail.overview.problemLabel': "Problem / Need Description",
    'projectDetail.overview.problemPlaceholder': "What problem or need does this initiative address?",
    'projectDetail.overview.goalLabel': "Goal",
    'projectDetail.overview.goalPlaceholder': "What is the desired outcome?",
    'projectDetail.overview.valueAddedLabel': "Value Added",

    // ── projectDetail: Overview tab — deliverable / resources / ownership ──
    'projectDetail.overview.deliverableSectionLabel': "Deliverable",
    'projectDetail.overview.deliverableFieldLabel': "What will exist at the end",
    'projectDetail.overview.resourcesSectionLabel': "Resources & Effort (Estimate)",
    'projectDetail.overview.effortSizeLabel': "Effort Size (Man-Days)",
    'projectDetail.overview.timelineLabel': "Timeline Until Completed",
    'projectDetail.overview.cashBudgetLabel': "Cash Budget",
    'projectDetail.overview.cashBudgetPlaceholder': "e.g. 50k, 200k EUR",
    'projectDetail.overview.teamAllocationLabel': "Team allocation",
    'projectDetail.overview.collaboratorsLabel': "Collaborators",
    'projectDetail.overview.ownershipSectionLabel': "Ownership",
    'projectDetail.overview.sponsorLabel': "Sponsor",
    'projectDetail.overview.sponsorPlaceholder': "Who sponsors this?",
    'projectDetail.overview.pmoAccompanistLabel': "PMO Accompanist",
    'projectDetail.overview.pmoAccompanistPlaceholder': "Choose an accompanist",
    'projectDetail.overview.userGroupsLabel': "User / User Groups",
    'projectDetail.overview.userGroupsPlaceholder': "Type and press Enter",

    // ── projectDetail: Préqualification tab — decision ──
    'projectDetail.prequalDecision.sectionLabel': "Decision",
    'projectDetail.prequalDecision.contactedSponsor': "Sponsor contacted for clarifications",
    'projectDetail.prequalDecision.help': "Take a decision when ready — this saves your préqualification work and moves the project to the next stage.",
    'projectDetail.prequalDecision.advanceButton': "Advance to Scoping →",
    'projectDetail.prequalDecision.deferButton': "Defer to backlog",
    'projectDetail.prequalDecision.currentVerdictLabel': "Current verdict: ",
    'projectDetail.prequalDecision.verdictAdvance': "Advance to Scoping",
    'projectDetail.prequalDecision.verdictDeferred': "Deferred",
    'projectDetail.prequalDecision.verdictLabel': "Verdict",
    'projectDetail.prequalDecision.advanceTitle': "Advance to Scoping",
    'projectDetail.prequalDecision.advanceModalMessage': "This saves your préqualification work and moves the project to the Scoping stage.",
    'projectDetail.prequalDecision.deferTitle': "Defer project",
    'projectDetail.prequalDecision.deferModalMessage': "This moves the project to Deferred — it will be reviewed at the next Conseil de Pilotage meeting.",
    'projectDetail.prequalDecision.deferConfirmLabel': "Defer",

    // ── projectDetail: Scoping tab ──
    'projectDetail.scoping.reminderLabel': "From the Overview (read-only)",
    'projectDetail.scoping.reminderDeliverables': "Deliverables",
    'projectDetail.scoping.sectionLabel': "Scoping",
    'projectDetail.scoping.inScopeLabel': "In scope",
    'projectDetail.scoping.inScopePlaceholder': "What is included in scope",
    'projectDetail.scoping.outOfScopeLabel': "Out of scope",
    'projectDetail.scoping.outOfScopePlaceholder': "What is excluded from scope",
    'projectDetail.scoping.urgencyLabel': "Urgency",
    'projectDetail.scoping.urgencyLow': "Low",
    'projectDetail.scoping.urgencyMedium': "Medium",
    'projectDetail.scoping.urgencyHigh': "High",
    'projectDetail.scoping.interventionLabel': "PMO intervention",
    'projectDetail.scoping.interventionWeekly': "Weekly",
    'projectDetail.scoping.interventionBiWeekly': "Bi-weekly",
    'projectDetail.scoping.interventionMonthly': "Monthly",
    'projectDetail.scoping.startMonth': "Start (month)",
    'projectDetail.scoping.endMonth': "End (month)",
    'projectDetail.scoping.cashRequested': "Cash budget (requested at intake)",
    'projectDetail.scoping.noneDash': "— none —",
    'projectDetail.scoping.availableBudgetLabel': "Available budget (confirmed with finance)",
    'projectDetail.scoping.availableBudgetPlaceholder': "e.g. 40k confirmed available",
    'projectDetail.scoping.financeNotesLabel': "Finance notes",
    'projectDetail.scoping.financeNotesPlaceholder': "Budget source, approvals, constraints…",
    'projectDetail.scoping.allocationsLabel': "Allocations",
    'projectDetail.scoping.allocHintWithWeeks': "{hpw}h week · {weeks} weeks over the time horizon",
    'projectDetail.scoping.allocHintNoWeeks': "{hpw}h week · set a start & end month above to compute hours",
    'projectDetail.scoping.colPerson': "Person",
    'projectDetail.scoping.colAllocation': "Allocation",
    'projectDetail.scoping.colHoursPerWeek': "h / week",
    'projectDetail.scoping.colTotalHours': "Total h",
    'projectDetail.scoping.rolePlaceholder': "role",
    'projectDetail.scoping.showOtherProjectsTitle': "Show this person's other projects",
    'projectDetail.scoping.removePersonTitle': "Remove person (also from intake)",
    'projectDetail.scoping.addPersonPlaceholder': "Add another person by name…",
    'projectDetail.scoping.totalHoursLabel': "Total hours",
    'projectDetail.scoping.totalHoursSetHorizon': "Total hours — set the time horizon",
    'projectDetail.scoping.advanceHelp': "When scoping is complete, advance to Qualification — this saves your changes, and the project then appears in the Conseil de Pilotage for the council to qualify.",
    'projectDetail.scoping.advanceButton': "Advance to Qualification →",
    'projectDetail.scoping.saveNeedsBothDates': "set a time-horizon start and end date",
    'projectDetail.scoping.saveNeedsStartDate': "set a time-horizon start date",
    'projectDetail.scoping.saveNeedsEndDate': "set a time-horizon end date",
    'projectDetail.scoping.saveNeedsPeople': "add at least one person to the allocations",
    'projectDetail.scoping.saveNeedsAllAllocations': "set every person's time allocation",
    'projectDetail.scoping.saveBlockedHint': "Scoping — can't save: {issues}",

    // ── projectDetail: Qualification tab ──
    'projectDetail.qualification.sectionLabel': "Qualification",
    'projectDetail.qualification.badgeBacklogged': "Backlogged",
    'projectDetail.qualification.badgeQualified': "Qualified for execution",
    'projectDetail.qualification.actionBacklogged': "Put into backlog",
    'projectDetail.qualification.actionQualified': "Qualified for execution",
    'projectDetail.qualification.onDate': "{label} on {date}",
    'projectDetail.qualification.pending': "Pending qualification by the Conseil de Pilotage.",
    'projectDetail.qualificationComingSoon': "Qualification details will be available here in a future update.",

    // ── projectDetail: Planning tab ──
    'projectDetail.planning.sectionLabel': "Planning",
    'projectDetail.planning.milestonesLabel': "Milestones",
    'projectDetail.planning.advanceHelp': "When planning is complete, advance the project into Execution — this saves your changes and moves the project forward.",
    'projectDetail.planning.advanceButton': "Advance to Execution →",

    // ── projectDetail: Execution tab ──
    'projectDetail.execution.statusLabel': "Project status",
    'projectDetail.execution.atRisk': "At risk — a milestone is overdue",
    'projectDetail.execution.onTrack': "On track",
    'projectDetail.execution.pmoStatusLabel': "PMO status",
    'projectDetail.execution.pmoStatusHelp': "Red / Orange / Green, assessed by the operator in PMO meetings. Shown on this project's dependency cards.",
    'projectDetail.execution.green': "Green",
    'projectDetail.execution.orange': "Orange",
    'projectDetail.execution.red': "Red",

    // ── projectDetail: Handover tab ──
    'projectDetail.handover.outcomeSectionLabel': "Outcome — improvements achieved",
    'projectDetail.handover.outcomeHint': "The improvements defined in Préqualification. Record the score actually achieved (defaults to target).",
    'projectDetail.handover.noImprovements': "No strategic improvements were defined for this project.",
    'projectDetail.handover.achievedLabel': "achieved",
    'projectDetail.handover.resourcesTitle': "Resources",
    'projectDetail.handover.notesLabel': "Handover notes",
    'projectDetail.handover.notesPlaceholder': "Outcome notes / lessons learned…",
    'projectDetail.handover.recordHelp': "Recording outcomes writes the achieved scores back to RCAJO (its PMO-scores bucket, dated & linked to this project) and closes the project.",
    'projectDetail.handover.recordButton': "Record outcomes in RCAJO & close →",

    // ── projectDetail: Footer ──
    'projectDetail.requestChange': "Request Change",
    'projectDetail.saveChanges': "Save Changes",
    'projectDetail.visibility': "Visibility",
    'projectDetail.visibilityPublic': "Public",
    'projectDetail.visibilityPrivate': "Private",
    'projectDetail.visibilityPrivateHelp': "Only collaborators involved in this project (and admins) can see it.",
    'projects.privateBadge': "Private project",
    'projectDetail.blockedMode': "Blocked mode",
    'projectDetail.blockedBadge': "Blocked",
    'projectDetail.blockedReasonPlaceholder': "Why is this project blocked?",
    'projectDetail.markBlocked': "Mark as blocked",
    'projectDetail.unblock': "Unblock",

    // ── projectDetail: confirm modals & stage-advance messages ──
    'projectDetail.advanceTitleTo': "Advance to {target}",
    'projectDetail.revertTitleTo': "Revert to {target}",
    'projectDetail.advance': "Advance",
    'projectDetail.revert': "Revert",
    'projectDetail.revertMessage': "Move the project back to {target}. Your work in the later stages is kept (just hidden) and reappears when you return to them.",
    'projectDetail.advanceForwardMessage': "Move the project forward to {target}. Any stages you skip stay empty until they're filled in.",
    'projectDetail.advanceMsg.qualification': "This moves the project to Qualification, where the Conseil de Pilotage reviews and qualifies it. Your scoping work is saved.",
    'projectDetail.advanceMsg.execution': "This moves the project into Execution. Your planning is saved.",
    'projectDetail.advanceMsg.handover': "All milestones are completed — this moves the project into Handover. Your work is saved.",
    'projectDetail.advanceMsg.generic': "This saves your work and moves the project to {target}.",
    'projectDetail.deleteInitiativeTitle': "Delete Initiative",
    'projectDetail.deleteInitiativeMessage': "Delete this initiative? This cannot be undone.",
    'projectDetail.recordCloseTitle': "Record outcomes & close",
    'projectDetail.recordCloseMessage': "This records the achieved scores in RCAJO (PMO-scores bucket, dated & linked to this project) and closes the project.",
    'projectDetail.recordAndClose': "Record & close",

    // ── projectForm: shared building blocks ──
    'projectForm.addQuoted': 'Add "{q}"',
    'projectForm.bullet.remove': "Remove",
    'projectForm.bullet.addOutput': "Add output",
    'projectForm.searchProjectsPlaceholder': "Search projects...",
    'projectForm.collaborators.label': "Collaborators Involved",
    'projectForm.collaborators.searchPlaceholder': "Type a name to search…",

    // ── projectForm: stage-gate "missing" field labels ──
    'projectForm.gate.name': "name",
    'projectForm.gate.problem': "problem",
    'projectForm.gate.goal': "goal",
    'projectForm.gate.deliverables': "deliverables",
    'projectForm.gate.valueAdded': "value added",
    'projectForm.gate.effortEstimate': "effort estimate",
    'projectForm.gate.sponsor': "sponsor",
    'projectForm.gate.verdictAdvance': 'a verdict of "Advance to Scoping"',
    'projectForm.gate.inScope': "in scope",
    'projectForm.gate.outOfScope': "out of scope",
    'projectForm.gate.urgency': "urgency",
    'projectForm.gate.interventionMode': "PMO intervention mode",
    'projectForm.gate.timeHorizon': "time horizon",
    'projectForm.gate.atLeastOneAllocation': "at least one allocation",
    'projectForm.gate.atLeastOneMilestone': "at least one milestone",
    // ── projectDetail: stage bar, topic tabs and Aperçu synthesis ──
    'projectDetail.stage.inbox': "Request",
    'projectDetail.stage.prequalification': "Prequalification",
    'projectDetail.stage.scoping': "Scoping",
    'projectDetail.stage.qualification': "Qualification",
    'projectDetail.stage.planning': "Planning",
    'projectDetail.stage.execution': "Execution",
    'projectDetail.stage.handover': "Handover",
    'projectDetail.stage.closed': "Closed",
    'projectDetail.stage.deferred': "Deferred",
    'projectDetail.stage.backlog': "Backlog",
    'projectDetail.stageBar.ariaSteps': "Project stages",
    'projectDetail.stageBar.toNext': "To move to {stage}",
    'projectDetail.stageBar.advanceTo': "Move to {stage}",
    'projectDetail.stageBar.completeFirst': "Complete the missing items first",
    'projectDetail.stageBar.jumpTo': "Go to where this is filled in",
    'projectDetail.stageBar.milestonesDone': "milestones done ({done}/{total})",
    'projectDetail.stageBar.executionHelp': "Milestones are closed in the follow-up of Plan & execution.",
    'projectDetail.stageBar.prequalTitle': "Prequalification decision",
    'projectDetail.stageBar.prequalHelp': "Assess the strategic value and the dependencies (To do tab), then decide. The decision saves your work and moves the project.",
    'projectDetail.stageBar.qualificationTitle': "Waiting for the Steering Council",
    'projectDetail.stageBar.qualificationHelp': "The Council qualifies the project for execution or puts it in the backlog.",
    'projectDetail.stageBar.openCouncil': "Open the Council",
    'projectDetail.stageBar.handoverTitle': "Closing the project",
    'projectDetail.stageBar.handoverHelp': "Enter the improvements achieved in the Closing tab, then record them in RCAJO.",
    'projectDetail.stageBar.openResults': "Enter the results",
    'projectDetail.stageBar.recordAndClose': "Record and close",
    'projectDetail.stageBar.closedTitle': "Project closed",
    'projectDetail.stageBar.deferredTitle': "Project deferred",
    'projectDetail.stageBar.deferredHelp': "It will be reviewed at the next Steering Council.",
    'projectDetail.stageBar.backlogTitle': "Project in the backlog",
    'projectDetail.stageBar.backlogHelp': "On hold: it resumes when capacity frees up.",
    'projectDetail.topic.apercu': "Overview",
    'projectDetail.topic.etape': "To do",
    'projectDetail.topic.etapeCurrent': "To do · {stage}",
    'projectDetail.topic.etapeOther': "Stage · {stage}",
    'projectDetail.stageBar.viewStage': "Open the {stage} stage: what it asks for",
    'projectDetail.stageView.titleCurrent': "{stage} · what to do",
    'projectDetail.stageView.titlePast': "{stage} · stage completed",
    'projectDetail.stageView.titleFuture': "{stage} · upcoming stage",
    'projectDetail.stageView.backToCurrent': "Back to the current stage ({stage})",
    'projectDetail.stageView.pastNote': "This information stays editable. Every change is recorded in the project history.",
    'projectDetail.stageView.futureNote': "This stage opens later; the project is at {stage}. Here is what it will ask for:",
    'projectDetail.stageView.optional': "optional",
    'projectDetail.stageView.strategic': "Strategic check",
    'projectDetail.stageView.sequencing': "Dependencies and sequencing",
    'projectDetail.stageView.decision': "Decision",
    'projectDetail.stageView.next': "Move to the next stage",
    'projectDetail.stageView.help.Inbox': "Describe the request: the problem and objective, what will be delivered, the sponsor and the estimated effort. Once these are filled in, the request moves to Pre-qualification.",
    'projectDetail.stageView.help.Préqualification': "Assess the request's strategic value (RCAJO capabilities and resources) and its dependencies on other projects, then decide: continue to Scoping or defer it.",
    'projectDetail.stageView.help.Scoping': "Set the scope, the calendar and the team with its allocations. The project then goes to Qualification before the Steering Council.",
    'projectDetail.stageView.help.Qualification': "The Steering Council reviews the project and qualifies it for execution or puts it in the backlog. The decision is taken in the Council.",
    'projectDetail.stageView.help.Planning': "Break the project into milestones that cover the deliverables. At least one milestone is needed to move to Execution.",
    'projectDetail.stageView.help.Handover': "Record the results achieved and the handover notes, then close the project.",
    'projectDetail.stageSetTitle': "Project stage",
    'projectDetail.history.button': "History ({n})",
    'projectDetail.history.title': "Changed after its stage",
    'projectDetail.history.empty': "(empty)",
    'projectDetail.history.field.problem': "Problem description",
    'projectDetail.history.field.goal': "Objective",
    'projectDetail.history.field.value_added': "Value added",
    'projectDetail.history.field.deliverables': "Deliverables",
    'projectDetail.history.field.effort_size': "Effort estimate",
    'projectDetail.history.field.timeline': "Time to completion",
    'projectDetail.history.field.cash': "Budget requested",
    'projectDetail.history.field.pmo_accompanist': "PMO accompanist",
    'projectDetail.history.field.user_group': "User groups",
    'projectDetail.history.field.contacted_sponsor': "Sponsor contacted",
    'projectDetail.history.field.prequal_notes': "Pre-qualification notes",
    'projectDetail.history.field.rcajo': "Strategic check",
    'projectDetail.history.field.sequencing': "Dependencies and sequencing",
    'projectDetail.history.field.scope_includes': "In scope",
    'projectDetail.history.field.scope_excludes': "Out of scope",
    'projectDetail.history.field.urgency': "Urgency",
    'projectDetail.history.field.intervention_mode': "PMO intervention",
    'projectDetail.history.field.time_horizon_start': "Start (month)",
    'projectDetail.history.field.time_horizon_end': "End (month)",
    'projectDetail.history.field.available_budget': "Budget available",
    'projectDetail.history.field.cash_notes': "Finance notes",
    'projectDetail.history.field.allocations': "Team allocations",
    'projectDetail.history.field.check_in_cadence': "Check-in cadence",
    'projectDetail.history.field.health': "PMO status",
    'projectDetail.history.field.handover_notes': "Handover notes",
    'projectDetail.stageSetHelp': "Puts the project directly at a stage, skipping its conditions (PMO). The change is recorded in the history.",
    'projectDetail.topic.contenu': "Content",
    'projectDetail.topic.equipe': "Team & resources",
    'projectDetail.topic.strategie': "Strategy",
    'projectDetail.topic.plan': "Plan & execution",
    'projectDetail.topic.cloture': "Closing",
    'projectDetail.topics.lockedUntil': "To complete at the {stage} stage",
    'projectDetail.topics.opensAt': "Opens at the {stage} stage",
    'projectDetail.topics.lockedDesc.apercu': "",
    'projectDetail.topics.lockedDesc.contenu': "",
    'projectDetail.topics.lockedDesc.equipe': "",
    'projectDetail.topics.lockedDesc.strategie': "The strategic assessment (RCAJO), sequencing and the prequalification decision.",
    'projectDetail.topics.lockedDesc.plan': "The project's milestones, then their follow-up with tasks and documents during execution.",
    'projectDetail.topics.lockedDesc.cloture': "The improvements achieved and the handover notes, recorded when the project closes.",
    'projectDetail.topics.problemGoal': "Problem and goal",
    'projectDetail.topics.classification': "Classification",
    'projectDetail.topics.team': "Team",
    'projectDetail.topics.allocationsLater': "Roles and allocation percentages are set at the {stage} stage.",
    'projectDetail.topics.calendar': "Timing",
    'projectDetail.topics.budgetEffort': "Budget and effort",
    'projectDetail.topics.followUp': "Follow-up",
    'projectDetail.topics.editPlan': "Edit the plan",
    'projectDetail.topics.settings': "Project settings",
    'projectDetail.topics.unsaved': "Unsaved changes",
    'projectDetail.topics.allSaved': "All changes saved",
    'projectDetail.synth.atStage': "at the {stage} stage",
    'projectDetail.synth.health': "PMO status",
    'projectDetail.synth.nextMilestone': "Next milestone",
    'projectDetail.synth.allMilestonesDone': "All done",
    'projectDetail.synth.progress': "Progress",
    'projectDetail.synth.milestonesCount': "{done} / {total} milestones",
    'projectDetail.synth.projectBlock': "The project",
    'projectDetail.synth.scopeIn': "In",
    'projectDetail.synth.scopeOut': "Out",
    'projectDetail.synth.classification': "Classification",
    'projectDetail.synth.team': "Team",
    'projectDetail.synth.horizon': "Horizon",
    'projectDetail.synth.requested': "(requested)",
    'projectDetail.synth.budgetRequested': "Budget requested",
    'projectDetail.synth.budgetAvailable': "Budget available",
    'projectDetail.synth.decisions': "Decisions",
    'projectDetail.synth.pending': "Pending",
    'projectDetail.synth.pendingInBar': "Taken in the stage bar",
    'projectDetail.synth.edit': "Edit",
    'projectDetail.synth.open': "Open",
    'projectDetail.synth.plannedEnd': "Planned end",
    'projectDetail.synth.milestones': "Milestones",
    'projectDetail.synth.milestonesLater': "Milestones are defined at the {stage} stage.",
    'projectDetail.synth.noMilestones': "No milestones yet.",
    'projectDetail.synth.noDeliverables': "No deliverables defined.",
    'projectDetail.synth.coveredBy': "Milestone {list}",
    'projectDetail.synth.ms.done': "Done",
    'projectDetail.synth.ms.active': "In progress",
    'projectDetail.synth.ms.late': "Late",
    'projectDetail.synth.ms.pending': "Upcoming",
    'projectDetail.topics.deliverablesToCover': "Deliverables to cover",
    'projectDetail.topics.deliverables': "Deliverables",
    'projectDetail.topics.milestonesAndTasks': "Milestones and tasks",
    'projectDetail.blockedReasonLabel': "Reason",
    'projectDetail.deleteInitiativeHelp': "Permanently deletes this initiative. This cannot be undone.",
  },
};
